import { Check, X } from "lucide-react";

/**
 * Desktop package tier card — rendered inside the 3-panel audience-swipe
 * carousel. Highlight tier gets a glowing border + hover-lift interaction.
 *
 * Props:
 *   tier: AUDIENCES[*].tiers item
 *   audienceId: e.g. "creators" | "brands" (for the data-testid)
 *   index: tier's position within its audience (0-2), used for label + testids
 */
export const PackageCardDesktop = ({ tier: t, audienceId, index: i }) => (
  <div
    data-tier-card
    data-testid={`package-${audienceId}-tier-${i}`}
    className={`relative rounded-3xl p-7 md:p-8 flex flex-col will-change-transform [transition:transform_500ms_cubic-bezier(0.16,1,0.3,1),opacity_500ms_ease-out,border-color_300ms_ease-out] group-hover/cards:scale-[0.97] group-hover/cards:opacity-80 hover:!scale-[1.045] hover:!opacity-100 hover:z-10 ${
      t.highlight
        ? "glow-border bg-[#11091a]/80 -translate-y-3"
        : "bg-white/[0.03] border border-white/10 hover:border-white/20"
    } backdrop-blur-md`}
  >
    {t.highlight && (
      <>
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#00f0ff]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#ff0055]/20 blur-3xl pointer-events-none" />
      </>
    )}

    <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border"
          style={{
            color: t.accent,
            borderColor: `${t.accent}40`,
            background: `${t.accent}12`,
          }}
        >
          <t.icon size={12} /> {t.badge}
        </div>
        <div className="font-mono-tight text-[10px] text-white/40">
          0{i + 1}
        </div>
      </div>

      <div className="font-display font-bold text-[28px] md:text-[32px] tracking-tight">
        {t.name}
      </div>
      <div className="text-[#a395c3] text-sm mt-2 min-h-[42px]">
        {t.description}
      </div>

      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-display font-black text-5xl md:text-6xl tracking-tighter">
          ${t.price}
        </span>
        <span className="text-[#a395c3] text-sm">{t.unit}</span>
      </div>

      <a
        href="#book"
        onClick={(e) => {
          e.preventDefault();
          window.dispatchEvent(
            new CustomEvent("sb:open-booking", {
              detail: { package: t.pkg },
            })
          );
        }}
        data-testid={`package-cta-${audienceId}-${i}`}
        className={`mt-7 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold text-sm transition-all ${
          t.highlight
            ? "bg-[#00f0ff] text-[#05050a] hover:bg-white"
            : "bg-white/[0.05] border border-white/15 hover:bg-white/[0.1]"
        }`}
      >
        {t.cta}
      </a>

      {t.pkg && t.pkg !== "custom" && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent("sb:open-buy", { detail: { package_key: t.pkg } }));
          }}
          data-testid={`package-buy-${audienceId}-${i}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-xs text-white/70 hover:text-white bg-transparent border border-white/10 hover:border-[#22c55e]/40 hover:bg-[#22c55e]/[0.06] transition-all"
        >
          Buy now →
        </button>
      )}

      <ul className="mt-7 pt-7 border-t border-white/10 space-y-3.5">
        {t.features.map((f) => (
          <li
            key={f.label}
            className={`flex items-start gap-3 text-sm ${
              f.in ? "text-white/90" : "text-white/35 line-through"
            }`}
          >
            {f.in ? (
              <Check
                size={16}
                className="text-[#00f0ff] mt-0.5 flex-shrink-0"
                strokeWidth={2.5}
              />
            ) : (
              <X
                size={16}
                className="text-white/30 mt-0.5 flex-shrink-0"
                strokeWidth={2}
              />
            )}
            <span>{f.label}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export default PackageCardDesktop;
